import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import SegmentUtils from '@spothero/utils/segment';

/**
 * Hook that handles Segment event tracking
 * @function useSegment
 * @returns {TUseSegmentEvents} - Exposes the segment tracking functions
 *
 * @memberof module:hooks/segment
 *
 * @example
 * const {trackEvent, trackPage} = useSegment();
 *
 * trackEvent({
 *     event: 'event-name',
 *     properties: {foo: 'bar'}
 * });
 *
 * trackPage({
 *     name: 'page-name',
 *     properties: {foo: 'bar'}
 * });
 *
 */
const useSegment = () => {
    const {segment} = useSelector(({user}) => user);
    const isUser = segment.email?.length;

    /** @type {TTrackSegmentEvent} */
    const trackEvent = useCallback(
        ({event, properties}) => {
            if (!event || !isUser) {
                return;
            }

            SegmentUtils.track({
                event,
                properties: {
                    ...properties,
                    user: segment,
                },
            });
        },
        [isUser, segment]
    );

    /** @type {TTrackSegmentPage} */
    const trackPage = useCallback(
        ({name, properties}) => {
            if (!name || !isUser) {
                return;
            }

            SegmentUtils.page({
                name,
                properties: {
                    ...properties,
                    user: segment,
                },
            });
        },
        [isUser, segment]
    );

    return {trackEvent, trackPage};
};

export default useSegment;
